import SectionWrapper from "@/components/layouts/SectionWrapper";

/** Contenu aligné sur `docs/docs_juridiques_AGBAZA.md` (cookies & traceurs, AGBAZA). */
function AgbazaLegalDocPage() {
  return (
    <div
      data-theme="previn"
      className="min-h-full bg-base-100 font-rowdies text-base-content"
    >
      <div className="w-full pt-24 laptop:pt-28">
        <SectionWrapper
          id="document-juridique"
          className="overflow-visible"
          innerClassName="justify-start px-4 py-8 md:py-14"
        >
          <article className="prose prose-lg w-full max-w-3xl text-left text-base-content prose-headings:font-rowdies prose-headings:text-base-content">
            <p className="text-sm opacity-80 not-prose">
              Plateforme AGBAZA — Document juridique
            </p>
            <h1 className="not-prose text-3xl font-bold tracking-tight md:text-4xl">
              Politique de gestion des cookies et traceurs
            </h1>
            <p className="not-prose text-base opacity-90">
              Ce document complète les engagements du Ministère de la Santé en matière de
              protection des données et décrit, pour la plateforme AGBAZA, l&apos;usage des
              cookies et traceurs.
            </p>
            <p className="not-prose text-sm opacity-70">
              Date de mise à jour : 03/02/2026
            </p>

            <h2>1. Définition et objet</h2>
            <p>
              Dans un souci de transparence et de protection de votre vie privée, le
              Ministère de la Santé (Responsable de Traitement) et l&apos;établissement
              NUKONYIYI (Opérateur Technique) vous informent de l&apos;utilisation de
              cookies sur la plateforme AGBAZA.
            </p>
            <p>
              Qu&apos;est-ce qu&apos;un cookie ? Un &quot;cookie&quot; (ou traceur) est un
              petit fichier texte, totalement inoffensif pour votre appareil, déposé et lu
              par votre navigateur (ordinateur, tablette, smartphone) lors de la consultation
              de la plateforme. Il permet de retenir temporairement des informations
              concernant votre navigation (ex. : votre session connectée, votre choix de
              langue).
            </p>
            <p>
              Compte tenu de la nature sensible des données traitées (données de santé),
              AGBAZA utilise les cookies de manière restrictive et sécurisée, sans aucune
              visée commerciale ou publicitaire.
            </p>

            <h2>2. Types de cookies utilisés et finalités</h2>
            <p>
              Nous classons les cookies déposés sur votre terminal en trois catégories
              distinctes, selon leur nécessité :
            </p>

            <h3>2.1. Les cookies « strictement nécessaires » (techniques)</h3>
            <p>
              Ces cookies sont indispensables au bon fonctionnement de la plateforme et à la
              sécurité de vos données de santé. Ils ne requièrent pas votre consentement
              préalable (exemption légale) car la plateforme ne peut pas fonctionner sans eux.
            </p>
            <ul>
              <li>
                <strong>Authentification :</strong> permettent de vous identifier lorsque vous
                vous connectez à votre espace patient ou professionnel et de maintenir votre
                session ouverte pendant votre navigation.
              </li>
              <li>
                <strong>Sécurité :</strong> permettent de détecter des tentatives
                d&apos;intrusion et de sécuriser les formulaires (protection contre les
                attaques CSRF).
              </li>
              <li>
                <strong>Mémorisation des choix :</strong> enregistrent votre choix
                concernant les cookies (pour ne pas vous reposer la question à chaque page).
              </li>
            </ul>

            <h3>2.2. Les cookies de fonctionnalité (expérience utilisateur)</h3>
            <p>
              Ces cookies ne sont pas vitaux mais optimisent votre confort
              d&apos;utilisation.
            </p>
            <ul>
              <li>
                <strong>Préférences d&apos;affichage :</strong> mémorisation de la langue
                utilisée ou de la résolution d&apos;écran adaptée.
              </li>
              <li>
                <strong>Accessibilité :</strong> mémorisation des paramètres de contraste ou
                de taille de police pour les personnes malvoyantes.
              </li>
            </ul>

            <h3>2.3. Les cookies analytiques (statistiques anonymes)</h3>
            <p>
              Ces cookies nous permettent de connaître l&apos;utilisation et les performances
              de la plateforme pour en améliorer le fonctionnement.
            </p>
            <ul>
              <li>
                <strong>Finalité :</strong> comptabiliser le nombre de visiteurs, les pages
                les plus consultées (ex. : dossier médical, module vaccination) et détecter
                les erreurs de navigation (liens brisés).
              </li>
              <li>
                <strong>Confidentialité :</strong> les données collectées sont agrégées et
                anonymisées. Elles ne permettent pas de vous identifier personnellement ni de
                consulter vos données médicales.
              </li>
            </ul>
            <p>
              <strong>Important :</strong> aucun cookie publicitaire, commercial ou de
              partage vers les réseaux sociaux n&apos;est utilisé sur AGBAZA.
            </p>

            <h2>3. Durée de conservation</h2>
            <p>
              Conformément à la législation, la durée de vie des cookies est limitée au
              strict nécessaire :
            </p>
            <div className="not-prose overflow-x-auto">
              <table className="w-full min-w-[280px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-base-300">
                    <th className="py-2 pr-4 font-semibold">Type de cookie</th>
                    <th className="py-2 font-semibold">Durée sur votre appareil</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-b border-base-200">
                    <td className="py-3 pr-4">Cookies de session (connexion)</td>
                    <td className="py-3">
                      Expirent à la fermeture du navigateur ou après une période
                      d&apos;inactivité (déconnexion automatique de sécurité).
                    </td>
                  </tr>
                  <tr className="border-b border-base-200">
                    <td className="py-3 pr-4">Cookies de préférences</td>
                    <td className="py-3">Maximum 6 mois.</td>
                  </tr>
                  <tr className="border-b border-base-200">
                    <td className="py-3 pr-4">Cookies analytiques</td>
                    <td className="py-3">
                      Maximum 13 mois. Au-delà, les données brutes de fréquentation sont
                      supprimées ou anonymisées.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Choix du consentement (refus / accord)</td>
                    <td className="py-3">
                      Votre choix est conservé pendant 6 mois, après quoi le bandeau
                      réapparaîtra.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>4. Gestion de vos préférences (accepter ou refuser)</h2>
            <p>
              Lors de votre première visite sur AGBAZA, un bandeau d&apos;information apparaît
              pour recueillir votre choix. Vous disposez de plusieurs moyens pour gérer les
              cookies :
            </p>

            <h3>4.1. Via le module de gestion AGBAZA</h3>
            <p>
              Vous pouvez à tout moment modifier vos préférences en cliquant sur le lien
              « Gestion des cookies » situé en bas de chaque page du site ou dans les
              paramètres de votre profil utilisateur.
            </p>

            <h3>4.2. Via votre navigateur</h3>
            <p>
              Vous pouvez configurer votre logiciel de navigation pour rejeter
              systématiquement les cookies. La configuration de chaque navigateur est
              différente (consultez le menu d&apos;aide : Chrome, Firefox, Safari, Edge,
              etc.).
            </p>

            <h2>5. Impact du refus des cookies</h2>
            <p>
              Votre liberté de choix est totale. Cependant, nous attirons votre attention sur
              les conséquences du refus :
            </p>
            <ul>
              <li>
                <strong>Refus des cookies analytiques :</strong> sans conséquence sur votre
                utilisation. Cela nous prive simplement de données pour améliorer le service
                public.
              </li>
              <li>
                <strong>Refus des cookies strictement nécessaires :</strong> si vous
                désactivez tous les cookies via votre navigateur (y compris les cookies
                techniques), vous ne pourrez plus vous connecter à votre espace sécurisé ni
                accéder à votre dossier médical. La navigation sera fortement dégradée, voire
                impossible, pour des raisons de sécurité.
              </li>
            </ul>

            <h2>6. Cookies tiers et sous-traitance</h2>
            <p>Dans le cadre strict de l&apos;architecture technique souveraine :</p>
            <ul>
              <li>l&apos;hébergement est assuré sur des infrastructures nationales sécurisées ;</li>
              <li>
                si un outil de mesure d&apos;audience tiers est utilisé, il est configuré pour
                ne pas transférer de données personnelles hors de l&apos;espace de confiance
                béninois et respecter l&apos;anonymat (conformité RGPD / APDP).
              </li>
            </ul>
            <p>
              Pour toute question relative à cette politique, vous pouvez contacter le
              Délégué à la Protection des Données : [adresse e-mail du DPO].
            </p>
          </article>
        </SectionWrapper>
      </div>
    </div>
  );
}

export default AgbazaLegalDocPage;
